import { z } from 'zod';

// Enhanced config schemas with better validation
const SlackConfig = z.object({
  channelId: z.string().min(1, 'Channel ID cannot be empty'),
  channelName: z.string().min(1, 'Channel name cannot be empty'),
});

const WebhookConfig = z.object({
  token: z.string().min(1, 'Token cannot be empty'),
  url: z.string().url('Must be a valid URL'),
  additionalFields: z.record(z.string(), z.any()).optional(),
});

// Enhanced status enum with better validation
const ChannelStatus = z.enum(['SUCCESS', 'FAILURE', 'PENDING'], {
  errorMap: () => ({ message: 'Status must be SUCCESS, FAILURE, or PENDING' }),
});

// Shared base channel fields with better validation
const BaseChannel = z.object({
  id: z.string().min(1, 'Channel ID cannot be empty'),
  serviceId: z.number().int().positive('Service ID must be a positive integer'),
  serviceName: z.string().min(1, 'Service name cannot be empty').optional(),
  status: ChannelStatus,
  errorMessage: z.string().optional(),
});

// Specific channel types using discriminated union best practices
export const SlackChannel = BaseChannel.extend({
  type: z.literal('slack'),
  config: SlackConfig,
});

export const WebhookChannel = BaseChannel.extend({
  type: z.literal('webhook'),
  config: WebhookConfig,
});

export const MissingChannel = BaseChannel.extend({
  type: z.literal('missing'),
}).omit({
  id: true,
});

// Discriminated union over "type"
export const Channel = z.discriminatedUnion('type', [
  SlackChannel,
  WebhookChannel,
  MissingChannel,
]);

export type GetChannelsResponse = z.infer<typeof GetChannelsResponse>;

export type SlackChannel = z.infer<typeof SlackChannel>;
export type WebhookChannel = z.infer<typeof WebhookChannel>;
export type MissingChannel = z.infer<typeof MissingChannel>;
export type Channel = z.infer<typeof Channel>;

export function isSlackChannel(channel?: Channel): channel is SlackChannel {
  return channel?.type === 'slack';
}

export function isWebhookChannel(channel?: Channel): channel is WebhookChannel {
  return channel?.type === 'webhook';
}

export function isMissingChannel(channel?: Channel): channel is MissingChannel {
  return channel?.type === 'missing';
}

export function isRealChannel(channel?: Channel): channel is RealChannel {
  return isSlackChannel(channel) || isWebhookChannel(channel);
}

export type RealChannel = SlackChannel | WebhookChannel;


export const GetChannelsResponse = z.object({
  channels: z.array(Channel),
});

export const DeleteChannelResponse = z.object({
  ok: z.literal(true),
});

export type DeleteChannelResponse = z.infer<typeof DeleteChannelResponse>;

// Update request channel schemas - omit id and status from base channels
const UpdateSlackChannel = SlackChannel.omit({
  id: true,
  status: true,
});

const UpdateWebhookChannel = WebhookChannel.omit({
  id: true,
  status: true,
});
// Discriminated union for update requests
const UpdateChannel = z.discriminatedUnion('type', [
  UpdateSlackChannel,
  UpdateWebhookChannel,
]);

export const UpdateChannelsRequest = z.object({
  channels: z.array(UpdateChannel),
});
export type UpdateSlackChannel = z.infer<typeof UpdateSlackChannel>;
export type UpdateWebhookChannel = z.infer<typeof UpdateWebhookChannel>;
export type UpdateChannel = z.infer<typeof UpdateChannel>;
export type UpdateChannelsRequest = z.infer<typeof UpdateChannelsRequest>;

export const UpdateChannelsResponse = z.object({
  ok: z.literal(true),
});
export type UpdateChannelsResponse = z.infer<typeof UpdateChannelsResponse>;
